import { createUniqueId } from 'shared/utils/id/id';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { channelClient } from '../channelContext';
import { RequestPolicies, type RequestPolicy } from '../requestPolicy';
import type {
  EventType,
  RequestMethod,
  RequestPayload,
  ChannelContract,
  ChannelContractShape,
  ContractEventOfType,
} from '../contract';

/** Contract of an entity whose changes run through the worker's HTTP queue. */
export interface QueuedMutationContract extends ChannelContractShape {
  events: ChannelContractShape['events'] & { [DomainMessagesTypes.failUploadedEntity]: unknown };
}

/** `uniqueId`s of the changes still waiting for their outcome on this page. */
const pendingMutationIds = new Set<string>();

/**
 * Whether a change of this page still waits for the outcome with this
 * `uniqueId`; when it does, its caller reports a failure, otherwise (after a
 * timeout, a reload, another tab) nobody would.
 */
export function isQueuedMutationPending(uniqueId: string | undefined): boolean {
  return uniqueId !== undefined && pendingMutationIds.has(uniqueId);
}

type MutationOutcome<TShape extends ChannelContractShape, TOutcomeType extends EventType<TShape>> = NonNullable<
  ContractEventOfType<TShape, TOutcomeType>['data']
>;

/** How a queued change is sent; defaults: `failUploadedEntity`, `RequestPolicies.mutation`. */
export interface QueuedMutationOptions<TFailureType> {
  /** The contract's own failure event, e.g. `processError`. */
  failureType?: TFailureType;
  /** e.g. `longRunning` for a change the server takes long to save. */
  policy?: RequestPolicy;
}

/**
 * Sends a change the worker saves through its HTTP queue. Resolves with the
 * data of the `outcomeType` event once the server accepted it, rejects with the
 * server's error and `messageId` when the queue reports the failure event
 * (`failUploadedEntity`, or the contract's own `failureType`, e.g. `processError`).
 * Both events carry the `uniqueId` created here.
 */
export function runQueuedMutation<
  TShape extends QueuedMutationContract,
  TMethod extends RequestMethod<TShape>,
  TOutcomeType extends EventType<TShape>,
>(
  contract: ChannelContract<TShape>,
  method: TMethod,
  toRequest: (uniqueId: string) => RequestPayload<TShape, TMethod>,
  outcomeType: TOutcomeType,
  options?: QueuedMutationOptions<never>
): Promise<MutationOutcome<TShape, TOutcomeType>>;
export function runQueuedMutation<
  TShape extends ChannelContractShape,
  TMethod extends RequestMethod<TShape>,
  TOutcomeType extends EventType<TShape>,
>(
  contract: ChannelContract<TShape>,
  method: TMethod,
  toRequest: (uniqueId: string) => RequestPayload<TShape, TMethod>,
  outcomeType: TOutcomeType,
  options: QueuedMutationOptions<EventType<TShape>> & { failureType: EventType<TShape> }
): Promise<MutationOutcome<TShape, TOutcomeType>>;
export async function runQueuedMutation<
  TShape extends ChannelContractShape,
  TMethod extends RequestMethod<TShape>,
  TOutcomeType extends EventType<TShape>,
>(
  contract: ChannelContract<TShape>,
  method: TMethod,
  toRequest: (uniqueId: string) => RequestPayload<TShape, TMethod>,
  outcomeType: TOutcomeType,
  {
    failureType = DomainMessagesTypes.failUploadedEntity as EventType<TShape>,
    policy = RequestPolicies.mutation,
  }: QueuedMutationOptions<EventType<TShape>> = {}
): Promise<MutationOutcome<TShape, TOutcomeType>> {
  const uniqueId = createUniqueId();
  pendingMutationIds.add(uniqueId);
  try {
    return await channelClient.command(
      contract,
      method,
      toRequest(uniqueId),
      { type: outcomeType, failureType, correlationId: uniqueId },
      policy
    );
  } finally {
    pendingMutationIds.delete(uniqueId);
  }
}
